/**
 * Sicht auf die Konversationsliste — der Client-Teil.
 *
 * Der Server liefert einem angemeldeten Menschen per Vorgabe NUR SEINE
 * Sitzungen (seit 11.09.2026 auch Admins; Rafael: "jeder User soll nur seine
 * Konversationen sehen"). Ein Admin kann mit `?scope=all` auf den Vollblick
 * schalten. Regeln + Begruendung: server/routes/shared/conversation-scope.ts.
 *
 * Hier liegt genau EINE Wahrheit fuer alle Listen (Mission Control, Queue,
 * Layout-Sync, Reiter-Aufmerksamkeit, Pyramide): der Schalter im localStorage.
 * Jeder Aufrufer baut seine Adresse ueber `conversationsUrl()` — dann zaehlen
 * Abzeichen und Listen dieselbe Menge, und kein Panel zeigt "alle", waehrend
 * das naechste "nur meine" zeigt.
 *
 * localStorage, nicht sessionStorage: Rafael hat sich beim Haus-Filter der
 * Guide-Flaeche zweimal beschwert, dass ein Schalter beim Hin- und Zurueckgehen
 * vergisst, was er eingestellt hat. Der Vollblick ist eine bewusste Wahl fuer
 * die Diagnose und soll stehen bleiben, bis er sie zuruecknimmt — auch ueber
 * ein Neuladen hinweg. Der Schalter zeigt seinen Zustand immer an, damit
 * "alle" nie unbemerkt aktiv bleibt.
 *
 * Der Parameter wird nur mitgeschickt, wenn der Server ihn annehmen wuerde:
 * fuer Admins. Ein liegengebliebener "alle"-Schalter in einem Browser, in dem
 * jetzt ein Partner angemeldet ist, darf dessen Listen nicht mit 403 leeren.
 */

export type ConversationScope = 'mine' | 'all';

const STORAGE_KEY = 'cui-conversation-scope';
export const SCOPE_CHANGED_EVENT = 'cui-conversation-scope-changed';

export function getConversationScope(): ConversationScope {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'all' ? 'all' : 'mine';
  } catch (err) {
    console.warn('[conversationScope] localStorage nicht lesbar — Vorgabe "mine":', err);
    return 'mine';
  }
}

export function setConversationScope(scope: ConversationScope): void {
  try {
    localStorage.setItem(STORAGE_KEY, scope);
  } catch (err) {
    console.warn('[conversationScope] localStorage nicht schreibbar — Wahl haelt nur bis zum Neuladen:', err);
  }
  window.dispatchEvent(new Event(SCOPE_CHANGED_EVENT));
}

/** Nur Admins duerfen `scope=all` senden — die Rolle setzt AuthContext nach /api/auth/me. */
export function canChooseConversationScope(): boolean {
  return window.__cuiUserRole === 'admin';
}

/** Der Sicht-Parameter, der an den Server geht — '' wenn nichts zu senden ist. */
export function conversationScopeParam(): string {
  return canChooseConversationScope() && getConversationScope() === 'all' ? 'scope=all' : '';
}

/**
 * Baut die Adresse der Konversationsliste mit den gegebenen Parametern plus
 * dem Sicht-Parameter. `base` ist der Pfad bis vor dem '?', z.B.
 * '/api/mission/conversations'.
 */
export function conversationsUrl(base: string, params: Record<string, string> = {}): string {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== '') qs.set(k, v);
  const scope = conversationScopeParam();
  if (scope) qs.set('scope', 'all');
  const s = qs.toString();
  return s ? `${base}?${s}` : base;
}

/** Ausgeblendet-Zaehlung, wie der Server sie neben der Liste liefert. */
export interface ConversationScopeReport {
  mode: ConversationScope;
  viewer: string | null;
  hidden: { otherUsers: number; unattributed: number; house: number; total: number };
  houseIncluded: number;
}

/** Kurztext fuer die Ausgeblendeten — '' wenn nichts ausgeblendet ist. */
export function describeHidden(report: ConversationScopeReport | undefined): string {
  if (!report || report.mode !== 'mine' || report.hidden.total === 0) return '';
  const teile: string[] = [];
  if (report.hidden.otherUsers) teile.push(`${report.hidden.otherUsers} von anderen`);
  if (report.hidden.unattributed) teile.push(`${report.hidden.unattributed} ohne Eigentümer`);
  if (report.hidden.house) teile.push(`${report.hidden.house} vom Haus`);
  return `${report.hidden.total} ausgeblendet (${teile.join(', ')})`;
}
